import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { transactionService } from '../../../services/transactionService';


const UpcomingView = ({ bills, onBulkTransactionsCreated }) => {
  const navigate = useNavigate();
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBills, setSelectedBills] = useState([]);
  const [creatingBulk, setCreatingBulk] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState({});

  useEffect(() => {
    generateUpcomingItems();
  }, [bills]);

  const generateUpcomingItems = async () => {
    try {
      setLoading(true);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      // Generate only the NEXT occurrence for each active recurring bill
      const nextOccurrences = [];
      bills?.forEach(bill => {
        if (bill?.status !== 'active') return;
        if (!bill?.nextExecutionDate) return;

        const nextDate = new Date(bill?.nextExecutionDate);
        if (isNaN(nextDate?.getTime())) return;

        nextOccurrences?.push({
          id: bill?.id,
          name: bill?.transactionName,
          amount: bill?.amount,
          date: nextDate,
          category: bill?.category,
          categoryColor: bill?.categoryColor,
          categoryIcon: bill?.categoryIcon,
          subcategory: bill?.subcategory,
          account: bill?.account,
          accountName: bill?.accountName,
          accountId: bill?.accountId,
          frequency: bill?.frequency,
          type: bill?.type,
          categoryId: bill?.categoryId,
          isPastDue: nextDate < today
        });
      });

      // Sort by date, earliest first
      nextOccurrences?.sort((a, b) => a?.date - b?.date);

      setUpcomingItems(nextOccurrences);
      
      // Check transaction existence for each item
      await checkTransactionsExistence(nextOccurrences);
    } catch (err) {
      console.error('Error generating upcoming items:', err);
      setUpcomingItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  const checkTransactionsExistence = async (items) => {
    try {
      const statusMap = {};
      
      for (const item of items) {
        if (!item?.accountId) continue;
        
        const hasTransaction = await checkTransactionExists(item);
        statusMap[item?.id] = hasTransaction;
      }
      
      setTransactionStatus(statusMap);
    } catch (err) {
      console.error('Error checking transactions existence:', err);
    }
  };
  
  const checkTransactionExists = async (item) => {
    try {
      if (!item?.accountId) return false;
      
      // Get all transactions for the item's account
      const transactions = await transactionService?.getByAccount(item?.accountId);
      
      if (!transactions || transactions?.length === 0) return false;
      
      // Format the target date as YYYY-MM-DD for same-day comparison
      const targetDateStr = item?.date?.toISOString()?.split('T')?.[0];
      
      // PRIMARY MATCHING: Check by recurring_transaction_id (most reliable)
      const matchByRecurringId = transactions?.some(txn => {
        // Must have recurring_transaction_id that matches this item
        if (!txn?.recurringTransactionId || txn?.recurringTransactionId !== item?.id) return false;
        
        // Must be on the same date
        const txnDateStr = txn?.date?.split('T')?.[0];
        return txnDateStr === targetDateStr;
      });
      
      if (matchByRecurringId) return true;
      
      // FALLBACK MATCHING: If no recurring_transaction_id match, use comprehensive matching
      const matchByAttributes = transactions?.some(txn => {
        // Extract date from transaction
        const txnDateStr = txn?.date?.split('T')?.[0];
        
        // Must match the exact date (same calendar day)
        if (txnDateStr !== targetDateStr) return false;
        
        // Must match account
        if (txn?.accountId !== item?.accountId) return false;
        
        // Must match type (income/expense)
        if (txn?.type !== item?.type) return false;
        
        // Match by amount (use ABS to avoid sign mismatch, allow 0.01 tolerance)
        const itemAmount = Math.abs(parseFloat(item?.amount) || 0);
        const txnAmount = Math.abs(parseFloat(txn?.amount) || 0);
        const amountMatch = Math.abs(itemAmount - txnAmount) < 0.01;
        
        if (!amountMatch) return false;
        
        // Optional: Match by category if both have it
        if (item?.categoryId && txn?.categoryId && item?.categoryId !== txn?.categoryId) {
          return false;
        }
        
        // All criteria matched
        return true;
      });
      
      return matchByAttributes;
    } catch (err) {
      console.error('Error checking transaction:', err);
      return false;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (date) => {
    if (!date || isNaN(date?.getTime())) return '';
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getStatusBadge = (item) => {
    const hasPaid = transactionStatus?.[item?.id];
    
    if (hasPaid) {
      return { label: 'Paid', bg: 'bg-success/10', text: 'text-success' };
    }
    
    if (item?.isPastDue) {
      return { label: 'Past Due', bg: 'bg-error/10', text: 'text-error' };
    }
    
    return { label: 'Scheduled', bg: 'bg-primary/10', text: 'text-primary' };
  };

  const pastDueItems = upcomingItems?.filter(item => item?.isPastDue && !transactionStatus?.[item?.id]);
  const hasPastDue = pastDueItems?.length > 0;

  const handleSelectBill = (billId) => {
    setSelectedBills(prev => {
      if (prev?.includes(billId)) {
        return prev?.filter(id => id !== billId);
      }
      return [...prev, billId];
    });
  };

  const handleSelectAllPastDue = (e) => {
    e?.stopPropagation();
    if (selectedBills?.length === pastDueItems?.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(pastDueItems?.map(item => item?.id));
    }
  };

  const handleCreateBulkTransactions = async () => {
    if (selectedBills?.length === 0) return;
    
    try {
      setCreatingBulk(true);
      const selectedItems = pastDueItems?.filter(item => selectedBills?.includes(item?.id));
      
      const results = [];
      const errors = [];
      
      // Create transactions for each selected item
      for (const item of selectedItems) {
        try {
          // Check one more time if transaction already exists (prevent duplicates)
          const alreadyExists = await checkTransactionExists(item);
          
          if (alreadyExists) {
            console.log(`Transaction already exists for ${item?.name}, skipping`);
            continue;
          }
          
          const transactionData = {
            accountId: item?.accountId,
            date: item?.date?.toISOString()?.split('T')?.[0],
            description: item?.name,
            amount: parseFloat(item?.amount),
            type: item?.type,
            categoryId: item?.categoryId,
            cleared: false,
            notes: 'Created from Bills & Recurring',
            recurringTransactionId: item?.id // CRITICAL: Link to recurring bill
          };
          
          const created = await transactionService?.create(transactionData);
          results?.push({ item, transaction: created });
        } catch (err) {
          console.error(`Error creating transaction for ${item?.name}:`, err);
          errors?.push({ item, error: err?.message });
        }
      }
      
      // Clear selection
      setSelectedBills([]);
      
      // Refresh the view
      if (onBulkTransactionsCreated) {
        await onBulkTransactionsCreated();
      }
      
      // Re-check transaction status
      await generateUpcomingItems();
      
      if (errors?.length > 0) {
        alert(`Created ${results?.length} transaction(s). Failed: ${errors?.length}`);
      } else if (results?.length > 0) {
        alert(`Successfully created ${results?.length} transaction(s)`);
      }
    } catch (err) {
      console.error('Error creating bulk transactions:', err);
      alert('Failed to create transactions: ' + err?.message);
    } finally {
      setCreatingBulk(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary" />
      </div>
    );
  }

  if (upcomingItems?.length === 0) {
    return (
      <div className="text-center py-32">
        <div className="w-64 h-64 mx-auto mb-16 bg-muted/30 rounded-full flex items-center justify-center">
          <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-8">
          No Upcoming Payments
        </h3>
        <p className="text-sm text-muted-foreground">
          Your next scheduled recurring payments will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* Bulk Actions Bar */}
      {hasPastDue && (
        <div className="bg-card border border-border rounded-lg p-16">
          <div className="flex items-center justify-between gap-16">
            <div className="flex items-center gap-12">
              <Checkbox
                id="select-all-past-due"
                checked={selectedBills?.length === pastDueItems?.length && pastDueItems?.length > 0}
                onCheckedChange={(checked) => {
                  if (checked) {
                    setSelectedBills(pastDueItems?.map(item => item?.id));
                  } else {
                    setSelectedBills([]);
                  }
                }}
              />
              <label htmlFor="select-all-past-due" className="text-sm font-medium text-foreground cursor-pointer">
                Select All Past Due ({pastDueItems?.length})
              </label>
            </div>
            {selectedBills?.length > 0 && (
              <Button
                onClick={handleCreateBulkTransactions}
                disabled={creatingBulk}
                size="sm"
              >
                {creatingBulk ? (
                  <>
                    <Icon name="Loader" size={16} className="animate-spin mr-8" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Icon name="Plus" size={16} className="mr-8" />
                    Create {selectedBills?.length} Transaction{selectedBills?.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
      {/* Bills List */}
      <div className="space-y-2">
        {upcomingItems?.map(item => {
          const badge = getStatusBadge(item);
          const isSelected = selectedBills?.includes(item?.id);
          const hasPaid = transactionStatus?.[item?.id];
          const showCheckbox = item?.isPastDue && !hasPaid;

          return (
            <div 
              key={item?.id} 
              className={`bg-card border-b border-border p-12 hover:bg-muted/30 transition-colors ${
                isSelected ? 'ring-2 ring-primary' : ''
              }`}
            >
              <div className="flex items-start gap-12">
                {/* Checkbox for past due items */}
                {showCheckbox && (
                  <div className="flex items-center pt-8" onClick={(e) => e?.stopPropagation()}>
                    <Checkbox
                      id={`bill-${item?.id}`}
                      checked={isSelected}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedBills(prev => [...prev, item?.id]);
                        } else {
                          setSelectedBills(prev => prev?.filter(id => id !== item?.id));
                        }
                      }}
                    />
                  </div>
                )}

                {/* Icon */}
                <div 
                  className="w-36 h-36 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${item?.categoryColor || '#6366f1'}20` }}
                >
                  <Icon 
                    name={item?.categoryIcon || 'Tag'} 
                    size={18} 
                    color={item?.categoryColor || '#6366f1'} 
                  />
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-8 mb-4">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {item?.name || 'Unnamed'}
                    </h3>
                    <span className="text-sm font-bold text-foreground flex-shrink-0">
                      {formatCurrency(item?.amount)}
                    </span>
                  </div>

                  <div className="flex items-center gap-8 flex-wrap mb-4">
                    <div className={`inline-flex items-center px-6 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
                      {badge?.label}
                    </div>
                    {item?.type && (
                      <span className="text-xs text-muted-foreground capitalize">
                        {item?.type}
                      </span>
                    )}
                    {item?.category && (
                      <span className="text-xs text-muted-foreground">
                        {item?.category}
                      </span>
                    )}
                    {item?.subcategory && (
                      <span className="text-xs text-muted-foreground">
                        • {item?.subcategory}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-12 text-xs text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-4">
                      <Icon name="Calendar" size={12} color="var(--color-muted-foreground)" />
                      <span>{formatDate(item?.date)}</span>
                    </div>
                    {item?.accountName && (
                      <div className="flex items-center gap-4">
                        <Icon name="CreditCard" size={12} color="var(--color-muted-foreground)" />
                        <span>{item?.accountName}</span>
                      </div>
                    )}
                    {item?.frequency && (
                      <div className="flex items-center gap-4">
                        <Icon name="Repeat" size={12} color="var(--color-muted-foreground)" />
                        <span className="capitalize">{item?.frequency}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingView;